/*
 * "Meine Filiale": remembers the chosen store as its channel_id (the suffix of the
 * creativestyle.availability_store_<id> metafields). Read it through window.BMStore and
 * listen with document.addEventListener('store:selected', (e) => e.detail.store).
 */
(function () {
  const STORAGE_KEY = 'bm:selected-store';

  /* localStorage throws in private mode and embedded contexts; the picker must still work there. */
  function read() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      return parsed && parsed.channel ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function write(store) {
    try {
      if (store) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      /* Not persisted; `current` still holds it for this page. */
    }
  }

  /*
   * The page's copy of the selection, seeded from storage on first read. Storage can
   * fail on write while reads still return an older value, so once set, this wins.
   */
  let current;

  const BMStore = {
    get() {
      if (current === undefined) current = read();
      return current;
    },

    set(store) {
      current = store || null;
      write(store);
      document.dispatchEvent(new CustomEvent('store:selected', { detail: { store: current } }));
    },

    /*
     * The drawer is rendered once per breakpoint (desktop pill, mobile bar). Only the
     * visible one may be clicked: opening a hidden one shows nothing but still locks
     * page scroll. getClientRects, unlike offsetParent, also sees triggers inside a
     * fixed header.
     */
    openPicker() {
      const triggers = document.querySelectorAll('.store-selector--trigger summary, .store-selector--bar summary');
      const trigger = Array.from(triggers).find((element) => element.getClientRects().length > 0);

      if (trigger) trigger.click();
    },
  };

  window.BMStore = BMStore;

  class StoreSelector extends HTMLElement {
    connectedCallback() {
      this.radios = Array.from(this.querySelectorAll('.store-selector__radio'));
      this.radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange.bind(this)));

      this.items = Array.from(this.querySelectorAll('.store-selector__item'));

      this.clearButton = this.querySelector('[data-store-selector-clear] button');
      this.clearButton?.addEventListener('click', () => {
        BMStore.set(null);
        /* The button disables itself, so keep focus in the list instead of losing it. */
        this.radios[0]?.focus();
      });

      this.onStoreSelected = this.onStoreSelected.bind(this);
      document.addEventListener('store:selected', this.onStoreSelected);

      this.render(BMStore.get());

      /*
       * The chosen store is moved to the front only when the drawer opens, never on a
       * click inside it, so a tile can't jump out from under the pointer.
       */
      this.markLeading();
      this.drawer = this.closest('details');
      this.onDrawerToggle = () => {
        if (this.drawer.open) this.markLeading();
      };
      this.drawer?.addEventListener('toggle', this.onDrawerToggle);
    }

    disconnectedCallback() {
      document.removeEventListener('store:selected', this.onStoreSelected);
      this.drawer?.removeEventListener('toggle', this.onDrawerToggle);
    }

    markLeading() {
      const store = BMStore.get();
      const channel = store ? String(store.channel) : '';

      this.items.forEach((item) => {
        item.classList.toggle('store-selector__item--leading', item.dataset.storeChannel === channel);
      });
    }

    onRadioChange(event) {
      const radio = event.currentTarget;

      BMStore.set({
        channel: radio.value,
        name: radio.dataset.storeName || '',
      });

      this.closeDrawer();
    }

    /*
     * Choosing a store is the whole point of the drawer, so it closes itself
     * rather than leaving the shopper to find the X. Radios are not buttons, so
     * MenuDrawer's own "a click in here closes it" binding never sees them.
     *
     * Closing goes through MenuDrawer because it owns the open state, the focus
     * return and the body scroll lock; just removing [open] would strand the page
     * unscrollable. The drawer is an ancestor on the PDP and a descendant in the
     * header, hence both lookups.
     */
    closeDrawer() {
      const menuDrawer = this.closest('menu-drawer') || this.querySelector('menu-drawer');
      if (!menuDrawer) return;

      if (typeof menuDrawer.closeMenuDrawer === 'function') {
        menuDrawer.closeMenuDrawer(new Event('click'), menuDrawer.querySelector('summary'));
        return;
      }

      /* MenuDrawer has not upgraded yet; close the disclosure by hand. */
      const details = menuDrawer.querySelector('details');
      if (!details) return;

      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      document.body.classList.remove(
        'overflow-hidden-mobile',
        'overflow-hidden-tablet',
        'overflow-hidden-desktop'
      );
    }

    onStoreSelected(event) {
      this.render(event.detail.store);
    }

    render(store) {
      const channel = store ? String(store.channel) : '';

      this.radios.forEach((radio) => {
        radio.checked = radio.value === channel;
      });

      if (this.clearButton) this.clearButton.disabled = !channel;

      this.renderTrigger(store);
      this.classList.remove('store-selector--loading');
    }

    renderTrigger(store) {
      const label = this.querySelector('[data-store-selector-label]');
      const summary = this.querySelector('summary');
      const name = store && store.name ? store.name.trim() : '';

      if (summary) {
        summary.setAttribute('aria-label', name ? `Meine Filiale: ${name}. Filiale wechseln` : 'Wähle deine Filiale');
      }

      /* The mobile trigger is icon only and has no label to swap. */
      if (!label) return;

      label.textContent = '';

      if (!name) {
        label.textContent = label.dataset.defaultLabel || '';
        return;
      }

      const words = name.split(/\s+/);
      const branch = words.pop();
      const prefix = words.join(' ');

      if (prefix) {
        const prefixElement = document.createElement('span');
        prefixElement.textContent = prefix;
        label.appendChild(prefixElement);
      }

      const branchElement = document.createElement('strong');
      branchElement.className = 'store-selector__trigger-name';
      branchElement.textContent = branch;
      label.appendChild(branchElement);
    }
  }

  if (!customElements.get('store-selector')) {
    customElements.define('store-selector', StoreSelector);
  }

  /* PDP store row: the chosen store and its availability, from JSON embedded by 'product-add-to-basket'. */
  class StoreAvailabilityLine extends HTMLElement {
    connectedCallback() {
      this.data = this.readData();
      this.chosenParts = Array.from(this.querySelectorAll('.product__store-line--chosen'));
      this.emptyParts = Array.from(this.querySelectorAll('.product__store-line--none'));
      this.nameElement = this.querySelector('[data-store-line-name]');
      this.stateElement = this.querySelector('[data-store-line-state]');

      /* Both states offer a way into the picker: "Filiale wählen" and "Filiale ändern". */
      this.querySelectorAll('[data-store-picker-open]').forEach((element) =>
        element.addEventListener('click', () => BMStore.openPicker())
      );

      this.onStoreSelected = this.onStoreSelected.bind(this);
      document.addEventListener('store:selected', this.onStoreSelected);

      this.render(BMStore.get());
    }

    disconnectedCallback() {
      document.removeEventListener('store:selected', this.onStoreSelected);
    }

    readData() {
      try {
        return JSON.parse(this.querySelector('[data-store-availability]').textContent);
      } catch (error) {
        return { labels: {}, states: {} };
      }
    }

    onStoreSelected(event) {
      this.render(event.detail.store);
    }

    render(store) {
      const hasStore = Boolean(store && store.channel);

      this.chosenParts.forEach((element) => {
        element.hidden = !hasStore;
      });

      this.emptyParts.forEach((element) => {
        element.hidden = hasStore;
      });

      if (!hasStore) {
        if (this.stateElement) this.stateElement.hidden = true;

        /*
         * The name doubles as the drawer's summary, and CSS hides it while empty.
         * Clearing it matters when a store is unset again, or a stale name would
         * keep showing with nothing behind it.
         */
        if (this.nameElement) this.nameElement.textContent = '';
        return;
      }

      if (this.nameElement) this.nameElement.textContent = store.name;

      if (!this.stateElement) return;

      const state = this.data.states[String(store.channel)] || '';
      const label = this.data.labels[state] || '';

      this.stateElement.textContent = label;
      this.stateElement.hidden = !label;
      this.stateElement.className = state
        ? `product__availability-info product__availability-info--${state}`
        : 'product__availability-info';
    }
  }

  if (!customElements.get('store-availability-line')) {
    customElements.define('store-availability-line', StoreAvailabilityLine);
  }

  /*
   * The whole delivery box is a click target, forwarding to the drawer it opens:
   * shipping costs on the left, store availability on the right, whether or not a
   * Filiale has been chosen. The underlined text stays the visible affordance;
   * this only widens where a pointer may land.
   *
   * "Filiale wählen" is excluded by the control check below, so it still opens the
   * picker rather than the availability drawer.
   */
  document.addEventListener('click', (event) => {
    const box = event.target.closest('.product__delivery-option');
    if (!box) return;

    /*
     * An open drawer is still a descendant of the box, so its own content -- an
     * address, opening hours, a shipping cost -- would otherwise forward a click
     * to the opener and close the drawer the moment it is read.
     */
    if (event.target.closest('.side-drawer')) return;

    /* Controls keep their own behaviour, including the opener itself. */
    if (event.target.closest('summary, button, a, input, label')) return;

    const trigger = box.querySelector('summary');

    if (trigger) trigger.click();
  });
})();
